import { useState, useMemo } from 'react';
import { Button, Card, Flex, Select, Stack, useTheme, spacing } from '@fti/design-system';
import { useSimulationResults, useExportSimulations, useExecution } from '../../../api/media-sim/executions';
import { ExportDropdown } from '../shared/ExportDropdown';
import type { SimulationResult } from '../../../types/media-sim';

interface SimulationResultsProps {
  projectId: string;
  runId: string;
  executionId: string;
}

const SECTION_LABEL_STYLE = {
  fontSize: '0.75rem',
  fontWeight: 600 as const,
  textTransform: 'uppercase' as const,
  margin: 0,
};

function extractJsonField(text: string, fieldName: string): string | null {
  const keyPattern = `"${fieldName}"\\s*:\\s*"`;
  const keyMatch = text.match(new RegExp(keyPattern));
  if (!keyMatch || keyMatch.index === undefined) return null;

  const valueStart = keyMatch.index + keyMatch[0].length;
  const remaining = text.slice(valueStart);
  const endMatch = remaining.match(/"\s*(?:,\s*\n\s*"|,?\s*\n\s*\})/);
  if (endMatch && endMatch.index !== undefined) {
    return remaining.slice(0, endMatch.index).replace(/\\n/g, '\n').replace(/\\"/g, '"').replace(/\\\\/g, '\\');
  }
  const lastQuote = remaining.lastIndexOf('"');
  if (lastQuote > 0) {
    return remaining.slice(0, lastQuote).replace(/\\n/g, '\n').replace(/\\"/g, '"').replace(/\\\\/g, '\\');
  }
  return null;
}

function parseArticleFields(sim: SimulationResult) {
  let articleBody = sim.article_body || '';
  let editorialPlan = sim.editorial_plan || '';

  if (articleBody && !editorialPlan) {
    let text = articleBody.trim();
    const fenceMatch = text.match(/^```(?:json)?\s*\n?([\s\S]*?)\n?\s*```$/);
    if (fenceMatch) text = fenceMatch[1].trim();

    if (text.startsWith('{')) {
      try {
        const parsed = JSON.parse(text);
        if (parsed.article_body) articleBody = parsed.article_body;
        if (parsed.editorial_plan) editorialPlan = parsed.editorial_plan;
      } catch {
        const extracted_body = extractJsonField(text, 'article_body');
        const extracted_plan = extractJsonField(text, 'editorial_plan');
        if (extracted_body) articleBody = extracted_body;
        if (extracted_plan) editorialPlan = extracted_plan;
      }
    }
  }

  return { articleBody, editorialPlan };
}

const MESSAGE_TAG_COLORS = [
  { bg: '#E8EAF6', text: '#283593' },  // indigo
  { bg: '#E0F2F1', text: '#00695C' },  // teal
  { bg: '#FFF3E0', text: '#E65100' },  // orange
  { bg: '#F3E5F5', text: '#6A1B9A' },  // purple
  { bg: '#E1F5FE', text: '#01579B' },  // light blue
  { bg: '#FBE9E7', text: '#BF360C' },  // deep orange
  { bg: '#E8F5E9', text: '#1B5E20' },  // green
  { bg: '#FCE4EC', text: '#880E4F' },  // pink
];

function ArticleCard({ sim, messageColor }: { sim: SimulationResult; messageColor?: { bg: string; text: string } }) {
  const { theme } = useTheme();
  const [expanded, setExpanded] = useState(false);
  const accepted = sim.editor_decision === true;
  const { articleBody, editorialPlan } = parseArticleFields(sim);

  return (
    <div
      style={{
        borderBottom: `1px solid ${theme.colors.border.default}`,
        padding: `${spacing.sm} 0`,
      }}
    >
      <Flex justify="between" align="center" gap="sm">
        <div style={{ flex: 1, minWidth: 0 }}>
          <p style={{ fontSize: '0.9rem', fontWeight: 500, color: theme.colors.text.primary, margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {sim.headline || 'No headline'}
          </p>
        </div>
        <Flex align="center" gap="xs" style={{ flexShrink: 0 }}>
          <span
            style={{
              fontSize: '0.7rem',
              fontWeight: 600,
              padding: `2px ${spacing.xs}`,
              borderRadius: 4,
              backgroundColor: accepted
                ? theme.colors.semantic.success.light
                : theme.colors.semantic.error.light,
              color: accepted
                ? theme.colors.semantic.success.default
                : theme.colors.semantic.error.default,
              whiteSpace: 'nowrap',
            }}
          >
            {accepted ? 'Accepted' : 'Rejected'}
          </span>
          {sim.message_name && messageColor && (
            <span
              style={{
                fontSize: '0.7rem',
                fontWeight: 600,
                padding: `2px ${spacing.xs}`,
                borderRadius: 4,
                backgroundColor: messageColor.bg,
                color: messageColor.text,
                whiteSpace: 'nowrap',
              }}
            >
              {sim.message_name}
            </span>
          )}
          <Button variant="ghost" size="sm" onClick={() => setExpanded(!expanded)}>
            {expanded ? 'Collapse' : 'View'}
          </Button>
        </Flex>
      </Flex>
      {expanded && (
        <div style={{ paddingTop: spacing.sm, paddingLeft: spacing.sm }}>
          <Stack gap="md">
            {sim.editor_reasoning && (
              <div>
                <p style={{ ...SECTION_LABEL_STYLE, color: theme.colors.text.secondary, marginBottom: spacing.xs }}>
                  Editor Reasoning
                </p>
                <p style={{ fontSize: '0.85rem', color: theme.colors.text.primary, margin: 0, fontStyle: 'italic' }}>
                  {sim.editor_reasoning}
                </p>
              </div>
            )}
            {articleBody && (
              <div>
                <p style={{ ...SECTION_LABEL_STYLE, color: theme.colors.text.secondary, marginBottom: spacing.xs }}>
                  Article Body
                </p>
                <div style={{ fontSize: '0.85rem', lineHeight: 1.6, color: theme.colors.text.primary, whiteSpace: 'pre-wrap' }}>
                  {articleBody}
                </div>
              </div>
            )}
            {editorialPlan && (
              <div>
                <p style={{ ...SECTION_LABEL_STYLE, color: theme.colors.text.secondary, marginBottom: spacing.xs }}>
                  Editorial Plan
                </p>
                <div
                  style={{
                    fontSize: '0.85rem',
                    lineHeight: 1.6,
                    color: theme.colors.text.primary,
                    whiteSpace: 'pre-wrap',
                    backgroundColor: theme.colors.background.paper,
                    padding: spacing.sm,
                    borderRadius: '4px',
                    border: `1px solid ${theme.colors.border.default}`,
                  }}
                >
                  {editorialPlan}
                </div>
              </div>
            )}
          </Stack>
        </div>
      )}
    </div>
  );
}

type MessageColorMap = Map<string, { bg: string; text: string }>;

function PublicationArticleGroup({ publisher, articles, messageColors }: { publisher: string; articles: SimulationResult[]; messageColors: MessageColorMap }) {
  const { theme } = useTheme();
  const [expanded, setExpanded] = useState(false);
  const accepted = articles.filter((s) => s.editor_decision === true).length;
  const rejected = articles.length - accepted;

  return (
    <Card variant="outlined">
      <Card.Header
        title={publisher}
        subtitle={`${articles.length} article(s) · ${accepted} accepted · ${rejected} rejected`}
        action={
          <button
            onClick={() => setExpanded(!expanded)}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              color: theme.colors.primary[500],
              fontSize: '0.85rem',
              fontWeight: 500,
            }}
          >
            {expanded ? 'Collapse' : 'Expand'}
          </button>
        }
      />
      {expanded && (
        <Card.Body>
          {articles.map((sim) => (
            <ArticleCard key={sim.id} sim={sim} messageColor={sim.message_name ? messageColors.get(sim.message_name) : undefined} />
          ))}
        </Card.Body>
      )}
    </Card>
  );
}

export function SimulationResults({ projectId, runId, executionId }: SimulationResultsProps) {
  const { theme } = useTheme();
  const [decisionFilter, setDecisionFilter] = useState('all');
  const [pubFilter, setPubFilter] = useState('all');
  const [msgFilter, setMsgFilter] = useState('all');
  const [search, setSearch] = useState('');
  const { downloadExport, loading: exporting } = useExportSimulations();
  const { execution } = useExecution(projectId, runId, executionId);
  const { results, loading, error } = useSimulationResults(projectId, runId, executionId, {
    editorDecision: decisionFilter !== 'all' ? decisionFilter : undefined,
    limit: 200,
  });

  const simulations = results?.simulations ?? [];

  const q = search.trim().toLowerCase();
  const filteredSims = simulations.filter((sim) => {
    if (pubFilter !== 'all' && sim.publisher !== pubFilter) return false;
    if (msgFilter === '__none__' && sim.message_name) return false;
    if (msgFilter !== 'all' && msgFilter !== '__none__' && sim.message_name !== msgFilter) return false;
    if (q) {
      const haystack = [
        sim.headline,
        sim.article_body,
        sim.publisher,
        sim.message_name,
        sim.editorial_plan,
        sim.editor_reasoning,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      if (!haystack.includes(q)) return false;
    }
    return true;
  });

  const grouped = useMemo(() => {
    const map = new Map<string, SimulationResult[]>();
    for (const sim of filteredSims) {
      if (!map.has(sim.publisher)) map.set(sim.publisher, []);
      map.get(sim.publisher)!.push(sim);
    }
    return [...map.entries()].sort((a, b) => a[0].localeCompare(b[0]));
  }, [filteredSims]);

  const messageColorMap = useMemo(() => {
    const names = [...new Set(simulations.filter((s) => s.message_name).map((s) => s.message_name!))].sort();
    const map = new Map<string, { bg: string; text: string }>();
    names.forEach((name, i) => map.set(name, MESSAGE_TAG_COLORS[i % MESSAGE_TAG_COLORS.length]));
    return map;
  }, [simulations]);

  if (loading && !results) {
    return (
      <Card variant="outlined">
        <Card.Body>
          <p style={{ color: theme.colors.text.secondary, margin: 0 }}>Loading results...</p>
        </Card.Body>
      </Card>
    );
  }

  if (error) {
    return (
      <Card variant="outlined">
        <Card.Body>
          <p style={{ color: theme.colors.semantic.error.default, margin: 0 }}>
            {error.message || 'Failed to load results'}
          </p>
        </Card.Body>
      </Card>
    );
  }

  if (!results || simulations.length === 0) {
    return (
      <Card variant="outlined">
        <Card.Body>
          <p style={{ color: theme.colors.text.secondary, margin: 0 }}>
            No simulation results yet.
          </p>
        </Card.Body>
      </Card>
    );
  }

  const DECISION_OPTIONS = [
    { value: 'all', label: `All (${results.total_count})` },
    { value: 'accepted', label: `Accepted (${results.accepted_count})` },
    { value: 'rejected', label: `Rejected (${results.rejected_count})` },
  ];

  const publishers = [...new Set(simulations.map((s) => s.publisher))].sort();
  const PUB_OPTIONS = [
    { value: 'all', label: 'All Publications' },
    ...publishers.map((p) => ({ value: p, label: p })),
  ];

  const messages = [...new Set(simulations.filter((s) => s.message_name).map((s) => s.message_name!))].sort();
  const MSG_OPTIONS = [
    { value: 'all', label: 'All Messages' },
    { value: '__none__', label: 'No Message' },
    ...messages.map((m) => ({ value: m, label: m })),
  ];

  const acceptRate = results.total_count > 0
    ? Math.round((results.accepted_count / results.total_count) * 100)
    : 0;
  const costDollars = execution?.total_cost_cents
    ? (execution.total_cost_cents / 100).toFixed(2)
    : null;

  const statStyle = {
    textAlign: 'center' as const,
    flex: 1,
    minWidth: 100,
  };
  const statValue = {
    fontSize: '1.6rem',
    fontWeight: 700 as const,
    color: theme.colors.text.primary,
    margin: 0,
    lineHeight: 1.2,
  };
  const statLabel = {
    fontSize: '0.75rem',
    fontWeight: 500 as const,
    color: theme.colors.text.secondary,
    margin: 0,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.04em',
  };

  return (
    <Stack gap="md">
      {/* Summary header */}
      <h2 style={{ fontSize: '1.4rem', fontWeight: 600, color: theme.colors.text.primary, margin: 0 }}>
        {results.execution_type === 'full' ? 'Simulation' : 'Sample'} Results
      </h2>

      <Card variant="outlined">
        <Card.Body>
          <Flex justify="around" align="center" wrap="wrap" gap="md">
            <div style={statStyle}>
              <p style={statValue}>{results.total_count}</p>
              <p style={statLabel}>Articles</p>
            </div>
            <div style={statStyle}>
              <p style={statValue}>{publishers.length}</p>
              <p style={statLabel}>Publications</p>
            </div>
            <div style={statStyle}>
              <p style={{ ...statValue, color: theme.colors.semantic.success.default }}>{results.accepted_count}</p>
              <p style={statLabel}>Accepted</p>
            </div>
            <div style={statStyle}>
              <p style={{ ...statValue, color: theme.colors.semantic.error.default }}>{results.rejected_count}</p>
              <p style={statLabel}>Rejected</p>
            </div>
            <div style={statStyle}>
              <p style={statValue}>{acceptRate}%</p>
              <p style={statLabel}>Accept Rate</p>
            </div>
            {costDollars && (
              <div style={statStyle}>
                <p style={statValue}>${costDollars}</p>
                <p style={statLabel}>Estimated Cost</p>
              </div>
            )}
          </Flex>
        </Card.Body>
      </Card>

      {/* Article Outputs heading + export */}
      <Flex justify="between" align="center">
        <h2 style={{ fontSize: '1.4rem', fontWeight: 600, color: theme.colors.text.primary, margin: 0 }}>
          Article Outputs
        </h2>
        <ExportDropdown
          loading={exporting}
          onExport={(format) => downloadExport(projectId, runId, executionId, format, decisionFilter)}
        />
      </Flex>

      {/* Filters */}
      <Flex align="end" gap="sm" wrap="wrap">
        <div style={{ minWidth: 160 }}>
          <Select
            label="Editor Decision"
            options={DECISION_OPTIONS}
            value={decisionFilter}
            onChange={(value) => setDecisionFilter(value)}
          />
        </div>
        <div style={{ minWidth: 180 }}>
          <Select
            label="Publication"
            options={PUB_OPTIONS}
            value={pubFilter}
            onChange={(value) => setPubFilter(value)}
          />
        </div>
        {messages.length > 0 && (
          <div style={{ minWidth: 180 }}>
            <Select
              label="Message"
              options={MSG_OPTIONS}
              value={msgFilter}
              onChange={(value) => setMsgFilter(value)}
            />
          </div>
        )}
      </Flex>

      {/* Search — full width, below the filters */}
      <div>
        <label
          style={{
            display: 'block',
            marginBottom: '4px',
            fontSize: '0.8rem',
            fontWeight: 500,
            color: theme.colors.text.secondary,
          }}
        >
          Search Articles
        </label>
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search headline, body, plan, or reasoning..."
          style={{
            width: '100%',
            boxSizing: 'border-box',
            padding: `${spacing.xs} ${spacing.sm}`,
            border: `1px solid ${theme.colors.border.default}`,
            borderRadius: '4px',
            backgroundColor: theme.colors.background.paper,
            color: theme.colors.text.primary,
            fontSize: '0.9rem',
          }}
        />
      </div>

      {/* Grouped articles */}
      {grouped.length === 0 ? (
        <Card variant="outlined">
          <Card.Body>
            <p style={{ color: theme.colors.text.secondary, margin: 0 }}>No articles match the selected filters.</p>
          </Card.Body>
        </Card>
      ) : (
        grouped.map(([publisher, sims]) => (
          <PublicationArticleGroup key={publisher} publisher={publisher} articles={sims} messageColors={messageColorMap} />
        ))
      )}

      <div style={{ padding: `0 ${spacing.xs}` }}>
        <Stack gap="xs">
          <p style={{ margin: 0, fontSize: '0.75rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em', color: theme.colors.text.secondary }}>
            About cost
          </p>
          <p style={{ margin: 0, fontSize: '0.8rem', lineHeight: 1.6, color: theme.colors.text.secondary }}>
            The cost shown is an <strong>estimate</strong>, not exact billing. It combines the
            tokens used for reflection generation and article simulation across this execution,
            priced at approximate per-token rates. Actual charges depend on the specific models
            used at each stage and may differ.
          </p>
        </Stack>
      </div>
    </Stack>
  );
}
